import { useRef, useCallback } from 'react';

import {
  useBraintreeCreateMutation,
  BraintreeCreateRequest,
  BraintreeCreateResponse,
} from '@/api/payments/braintreeCreate';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { useCartContext } from '@/services/cart/CartContext';
import { useSite, SiteData, SiteUrls } from '@/services/site/useSite';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { setTransactionId } from '@/features/paymentProcess/transactionSlice';

import { separatePaymentDataName } from './googlepayUtils';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

const buildCreatePayload = (
  paymentSelectState: PaymentSelectState,
  paymentData: google.payments.api.PaymentData,
  client: string,
  siteData: SiteData,
  siteUrls: SiteUrls,
  campaignCode: string | null,
  isPayin: boolean,
  cartId?: string,
): BraintreeCreateRequest => ({
  client,
  order_reference: paymentSelectState.orderReference,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  giftaid: Number(paymentSelectState.giftaid),
  firstName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').firstName,
  lastName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').lastName,
  email: paymentData?.email ?? '',
  address1: paymentData?.shippingAddress?.address1 ?? '',
  address2: paymentData?.shippingAddress?.address2 ?? '',
  address3: paymentData?.shippingAddress?.address3 ?? '',
  town: paymentData?.shippingAddress?.locality ?? '',
  postcode: paymentData?.shippingAddress?.postalCode ?? '',
  country: paymentData?.shippingAddress?.countryCode ?? '',
  campaign: campaignCode ?? siteData.campaign.name,
  transSource: siteData.campaign.transSource,
  transSourceUrl: siteUrls.transSourceUrl,
  transType: isPayin ? 'payin' : siteData.campaign.transType,
  paymentType: 'GOOGLEPAY',
  paymentMethod: paymentData.paymentMethodData.info,
  recaptcha_token: 'dummytoken',
  recaptcha_version: 3,
  cartId: cartId ?? siteData.defaultCart,
  ...(!!paymentSelectState.fixedTransactionId && { transactionId: paymentSelectState.fixedTransactionId }),
});

export function useBraintreeCreate() {
  const onSuccess = useRef<((data?: BraintreeCreateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);
  const { cartId, getCartFinancialAttribution } = useCartContext();

  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const [braintreeCreateTrigger] = useBraintreeCreateMutation();

  const { getClientId } = useClient();
  const { getSiteData, getSiteUrls } = useSite();
  const { selectedJourney } = useJourneyOptions();
  const isPayin = selectedJourney === 'payin';

  // Trigger payment-service create
  const triggerCreateApi = useCallback(async (
    paymentData: google.payments.api.PaymentData,
    callbacks?: {
      onSuccess?: (data?: BraintreeCreateResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;
    const payload = buildCreatePayload(
      paymentSelectState,
      paymentData,
      getClientId(),
      getSiteData(),
      getSiteUrls(),
      getCartFinancialAttribution()?.campaignCode ?? null,
      isPayin,
      cartId,
    );
    const result = await braintreeCreateTrigger(payload);

    // Handle payment-service create errors
    if (!!result?.error || result?.data?.message !== 'success' || !result?.data?.data?.transactionId) {
      logger.error('Error creating Braintree transaction');
      logger.error(result?.error);

      if (onError.current) {
        onError.current(result?.error);
      }
      return null;
    }

    // Dispatch payment-service create data to transactionState
    dispatch(setTransactionId(result?.data.data.transactionId));

    if (onSuccess.current) {
      onSuccess.current(result?.data);
    }

    // Return transactionId for use in triggerExecuteApi()
    return result?.data?.data?.transactionId;
  }, [
    paymentSelectState,
    getClientId,
    getSiteData,
    getSiteUrls,
    getCartFinancialAttribution,
    braintreeCreateTrigger,
    dispatch,
    isPayin,
    cartId,
  ]);

  return {
    triggerCreateApi,
  };
}
